
import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { GOLD_PURITIES, SILVER_PURITIES, DEFAULT_GOLD_PURITY, DEFAULT_SILVER_PURITY } from '@/lib/constants';

// Re-export types
export * from '@/types/user';
export * from '@/types/dealer';
export * from '@/types/price';

// Import slices
import { PriceState, createPriceSlice } from './slices/price';
import { DealerState, createDealerSlice } from './slices/dealer';
import { UserState, createUserSlice } from './slices/auth/userSlice';

// Create the combined store type
export type StoreState = PriceState & DealerState & UserState;

// Create the store with all slices
export const useStore = create<StoreState>()(
  persist(
    (...args) => ({
      ...createPriceSlice(...args),
      ...createDealerSlice(...args),
      ...createUserSlice(...args),
    }),
    {
      name: 'gildy-store',
      version: 2, // Increment version to trigger migration and fix purity values
      migrate: (persistedState: any, version: number) => {
        console.log('[Store Migration] Running migration from version:', version);
        
        if (version < 2) {
          // Force reset delle purità ai valori corretti
          console.log('[Store Migration] Resetting purity values to defaults');
          persistedState.goldPurity = DEFAULT_GOLD_PURITY; // "24 KT"
          persistedState.silverPurity = DEFAULT_SILVER_PURITY; // "PURO"
        }
        
        // Verifica sempre che i valori delle purità siano validi
        const validGoldPurities = Object.keys(GOLD_PURITIES);
        if (!persistedState.goldPurity || !validGoldPurities.includes(persistedState.goldPurity)) {
          console.log('[Store Migration] Invalid gold purity, resetting to default:', persistedState.goldPurity);
          persistedState.goldPurity = DEFAULT_GOLD_PURITY;
        }

        const validSilverPurities = Object.keys(SILVER_PURITIES);
        if (!persistedState.silverPurity || !validSilverPurities.includes(persistedState.silverPurity)) {
          console.log('[Store Migration] Invalid silver purity, resetting to default:', persistedState.silverPurity);
          persistedState.silverPurity = DEFAULT_SILVER_PURITY;
        }
        
        console.log('[Store Migration] Final purity values:', {
          goldPurity: persistedState.goldPurity,
          silverPurity: persistedState.silverPurity
        });
        
        return persistedState;
      },
      partialize: (state) => ({
        // User preferences
        favoritesDealers: state.favoritesDealers,
        selectedTimePeriod: state.selectedTimePeriod,
        priceAlerts: state.priceAlerts,
        goldPurity: state.goldPurity,
        silverPurity: state.silverPurity,
        silverUnit: state.silverUnit,
        // Keep user session
        user: state.user,
        // Persist deprecated fields for now
        goldPrice: state.goldPrice,
        silverPrice: state.silverPrice,
        rawGoldPrice: state.rawGoldPrice,
        rawSilverPrice: state.rawSilverPrice,
      }),
    }
  )
);
